import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { CheckIcon, MinusIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Tabela em CSS GRID, não <table> — as colunas ficam alinhadas entre cabeçalho e linhas
 * porque ambos recebem o MESMO `columns`. Cada linha tem padding lateral de 22px,
 * hairline embaixo e hover bg-raised. Célula = py-[15px].
 */
const GridCtx = React.createContext<string>('');

/**
 * `caption` e `footer` (paginação) ficam FORA da área rolável — nunca somem com o scroll.
 * Só o miolo (cabeçalho + linhas) rola, com o cabeçalho grudado (`sticky top-0`) nesse miolo.
 */
export const DataGrid: React.FC<{
  columns: string;
  minWidth?: number;
  /** Altura máxima do miolo (cabeçalho + linhas) antes de rolar. Sem valor = cresce livre. */
  maxHeight?: string;
  caption?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ columns, minWidth = 0, maxHeight, caption, footer, children, className }) => (
  <GridCtx.Provider value={columns}>
    <div
      className={cn(
        'min-w-0 overflow-hidden rounded-[14px] border border-border bg-card',
        className,
      )}
    >
      {caption}
      <div className="overflow-auto" style={{ maxHeight }}>
        <div style={{ minWidth: minWidth || undefined }}>{children}</div>
      </div>
      {footer}
    </div>
  </GridCtx.Provider>
);

/** Faixa acima do cabeçalho: contagem à esquerda, ordenação à direita. */
export const GridCaption: React.FC<{
  children: React.ReactNode;
  trailing?: React.ReactNode;
}> = ({ children, trailing }) => (
  <div className="flex flex-col items-start gap-1.5 border-b border-border px-[22px] py-4 sm:flex-row sm:items-center sm:gap-3">
    <span className="col-label text-[10.5px] text-muted-foreground">{children}</span>
    {trailing && (
      <span className="flex flex-wrap items-center gap-[7px] text-[11.5px] font-semibold text-faint sm:ml-auto">
        {trailing}
      </span>
    )}
  </div>
);

export const GridHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="sticky top-0 z-10 grid border-b border-border bg-card px-[22px]"
    style={{ gridTemplateColumns: React.useContext(GridCtx) }}
  >
    {children}
  </div>
);

export const HeaderCell: React.FC<{
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  active?: boolean;
  onClick?: () => void;
  className?: string;
}> = ({ children, align = 'left', active, onClick, className }) => (
  <span
    onClick={onClick}
    className={cn(
      'col-label flex items-center py-[13px] text-[10px]',
      align === 'right' && 'justify-end text-right',
      align === 'center' && 'justify-center text-center',
      onClick && 'cursor-pointer select-none',
      active ? 'text-olive-ink' : 'text-faint',
      className,
    )}
  >
    {children}
  </span>
);

export const GridRow: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  onDoubleClick?: () => void;
}> = ({ children, onClick, onDoubleClick }) => (
  <div
    onClick={onClick}
    onDoubleClick={onDoubleClick}
    className="grid items-center border-b border-border px-[22px] transition-colors duration-150 last:border-b-0 hover:bg-raised"
    style={{ gridTemplateColumns: React.useContext(GridCtx) }}
  >
    {children}
  </div>
);

type CellProps = {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  /** `strong` = 14px branco (coluna-âncora); `meta` = 12.5px muted (datas de sistema). */
  tone?: 'default' | 'strong' | 'meta' | 'faint';
  truncate?: boolean;
  className?: string;
};

export const Cell: React.FC<CellProps> = ({
  children,
  align = 'left',
  tone = 'default',
  truncate,
  className,
}) => (
  <span
    className={cn(
      'py-[15px] text-[13.5px]',
      tone === 'default' && 'text-foreground/90',
      tone === 'strong' && 'text-sm font-semibold text-white',
      tone === 'meta' && 'text-[12.5px] text-muted-foreground',
      tone === 'faint' && 'text-xs font-semibold tracking-[.04em] text-faint',
      align === 'right' && 'text-right',
      align === 'center' && 'flex items-center justify-center',
      truncate && 'min-w-0 truncate',
      className,
    )}
  >
    {children}
  </span>
);

/** Valor monetário: 13px/700, vermelho para despesa, oliva para receita, areia para saldo positivo. */
export const MoneyCell: React.FC<{
  value: number;
  role: 'expense' | 'income' | 'balance';
}> = ({ value, role }) => {
  const text =
    (value < 0 ? '-' : '') +
    'R$ ' +
    Math.abs(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  return (
    <span
      className={cn(
        'py-[15px] text-right text-[13px] font-bold',
        role === 'expense' && 'text-destructive',
        role === 'income' && 'text-olive-ink',
        role === 'balance' &&
          (value > 0
            ? 'text-sand'
            : value === 0
              ? 'text-muted-foreground'
              : 'text-destructive'),
      )}
    >
      {text}
    </span>
  );
};

/** Botão quadrado de 32px das colunas de ação. */
export const RowAction: React.FC<{
  children: React.ReactNode;
  tone?: 'neutral' | 'accent' | 'danger';
  onClick?: () => void;
  title?: string;
}> = ({ children, tone = 'neutral', onClick, title }) => (
  <button
    type="button"
    title={title}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
    className={cn(
      'flex size-8 cursor-pointer items-center justify-center rounded-[9px] border border-border transition-all duration-150 hover:border-ring hover:bg-raised',
      tone === 'neutral' && 'text-muted-foreground',
      tone === 'accent' && 'text-olive-icon',
      tone === 'danger' && 'text-destructive',
    )}
  >
    {children}
  </button>
);

export const RowActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="flex justify-end gap-[7px] pr-1">{children}</span>
);

/** Checkbox de seleção em massa — mesmo componente/geometria do `checkbox.tsx` do form. */
export const RowCheckbox: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  indeterminate?: boolean;
  'aria-label'?: string;
}> = ({ checked, onChange, indeterminate, ...props }) => (
  <CheckboxPrimitive.Root
    checked={indeterminate ? 'indeterminate' : checked}
    onCheckedChange={(state) => onChange(state === true)}
    onClick={(e) => e.stopPropagation()}
    className="peer border-border-strong bg-raised data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground data-[state=indeterminate]:border-primary hover:border-border-strong focus-visible:border-primary focus-visible:ring-primary-soft size-4.5 shrink-0 cursor-pointer rounded-[5px] border shadow-none transition-colors outline-none focus-visible:ring-[4px]"
    {...props}
  >
    <CheckboxPrimitive.Indicator className="grid place-content-center text-current transition-none">
      {indeterminate ? (
        <MinusIcon className="size-3.5" />
      ) : (
        <CheckIcon className="size-3.5" />
      )}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
);
